import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'api/v1/restaurants/branches';
export const getWarehouseListEndpointIdentifier = endpoint;

type TData = Record<string, any>;
type TError = AxiosError;
type TQueryKey = [
  string,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    name: TParam;
  }
];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetBranchListApi(restaurantId: string, options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { name, sort } = getPageParams();
  const fullEndpoint = `${endpoint}/${restaurantId}`;

  const queryFn = async () => {
    try {
      const { data } = await http.get(fullEndpoint, {
        params: { page, limit, sort, name },
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([fullEndpoint, { page, limit, sort, name }], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
